import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import axios from "axios";
import { baseUrl } from "../../Network";
import { handleAuthError } from "../../utils";

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIMES_ = [
    "12:00AM", "1:00AM", "2:00AM", "3:00AM", "4:00AM", "5:00AM", "6:00AM", "7:00AM",
    "8:00AM", "9:00AM", "10:00AM", "11:00AM", "12:00PM", "1:00PM", "2:00PM", "3:00PM",
    "4:00PM", "5:00PM", "6:00PM", "7:00PM", "8:00PM", "9:00PM", "10:00PM", "11:00PM",
];

// Shared modal that lists every program belonging to a single child.
const ChildProgramsModal = ({ isOpen, toggle, childId, childName }) => {
    const [loading, setLoading] = useState(false);
    const [programs, setPrograms] = useState([]);

    useEffect(() => {
        if (!isOpen || !childId) return;
        let active = true;
        (async () => {
            try {
                setLoading(true);
                setPrograms([]);
                const res = await axios.get(`${baseUrl}/admin/child/${childId}/programs`);
                if (active) setPrograms(Array.isArray(res?.data?.data) ? res.data.data : []);
            } catch (error) {
                handleAuthError(error);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [isOpen, childId]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
            <ModalHeader toggle={toggle}>
                All programs{childName ? ` — ${childName}` : ""}
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : programs.length === 0 ? (
                    <div className="text-center text-muted py-4">No programs found for this child.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Package</th>
                                    <th>Level</th>
                                    <th>Teacher</th>
                                    <th>Cohort</th>
                                    <th>Day / Time</th>
                                    <th>Created</th>
                                    <th>Paid</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {programs.map((p, i) => (
                                    <tr key={p.id || i}>
                                        <td>{i + 1}</td>
                                        <td>{p?.package?.title || "—"}</td>
                                        <td>
                                            <span className="badge bg-info">
                                                {p?.package?.level ?? p?.level ?? "—"}
                                            </span>
                                        </td>
                                        <td>
                                            {p?.teacher
                                                ? `${p.teacher.firstName || ""} ${p.teacher.lastName || ""}`.trim()
                                                : "Not assigned"}
                                        </td>
                                        <td>{p?.cohort?.title || "—"}</td>
                                        <td>
                                            {typeof p?.day === "number" ? days[p.day] : "—"}
                                            {typeof p?.time === "number" ? ` ${TIMES_[p.time]}` : ""}
                                        </td>
                                        <td>{formatDate(p?.createdAt)}</td>
                                        <td>
                                            <span className={`badge ${p?.isPaid ? "bg-success" : "bg-secondary"}`}>
                                                {p?.isPaid ? "Paid" : "Unpaid"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${p?.isCompleted ? "bg-dark" : "bg-primary"}`}>
                                                {p?.isCompleted ? "Completed" : "Active"}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default ChildProgramsModal;
